import React, { useContext, useEffect, useRef, useState } from "react";
import { JSDCContext, JSDCProvider } from "jsdc-dui/dist/JSDC/Context";
import JSDC from "jsdc-dui/dist/JSDC";
import { GeoJSON, latLng, latLngBounds, Path } from "leaflet";
import {
  IDuiContextProviderProps,
  DuiContextProvider,
  DuiContext,
} from "jsdc-dui/dist/components/Context";
import DguideWalksApp from "jsdc-dui/dist/components/DguideWalksApp";
import {
  DguidewalksContext,
  DguidewalksProvider,
} from "jsdc-dui/dist/JSDC/Dguidewalks/Context";
import "jsdc-dui/dist/style/index.css";
import "./index.scss";
import ConfigProvider from "jsdc-dui/dist/JSDC/Dguidewalks/ConfigProvider";
import MenuItemWithDialog from "jsdc-dui/dist/components/LeftMenuBar/MenuList/MenuItemWithDialog";
import Checkin from "jsdc-dui/dist/components/Icons/Checkin";
import headerImg from "./assets/header.png";
import headerImgMb from "./assets/header-mb.jpg";
import kanbanImg from "./assets/kanban.png";
import aboutImg from "./assets/about.png";
const EVENT_ID = "s0006";

const aboutWalkContent = `成功大學立足於臺南獨特的人文時空，臺南位居臺灣進入世界貿易體系的重要地理時空位置，也是豐富的史前與原住民社會活動與棲息之地，有其得天獨厚的歷史與多元文化傳統。課程跨越學院的邊界，結合臺南府城的歷史形成與當代臺南都市發展的脈動與願景，實踐學校教學與社會脈動的連結。在專業知識學習之餘，走出校園藩籬的同時，與當代都市的脈動緊密結合，培養學生的在地關懷，應用與實踐跨越學科的社會價值。`;

const credit = `
<div>
    <ul>
        <li style="list-style-type: cjk-ideographic;">
          本圖台由踏溯臺南辦公室及智紳數位文化事業有限公司共同協力建置：
            <br><br>1. 圖臺內容：踏溯臺南辦公室及臺南市民提供。
            <br><br>2. 圖臺系統開發：智紳數位文化事業有限公司
        </li>
        <br><br>
        <li style="list-style-type: cjk-ideographic;">
        圖台內容其內容著作財產權由上述單位及個人保有並授權予踏溯臺南辦公室各項非商業行為使用。若有其他利用或授權需求請洽踏溯臺南辦公室。
        </li>
    </ul>
</div>
<div>地址：701臺南市大學路一號 修齊大樓七樓26704－踏溯臺南辦公室</div>
<div>電話：06-2757575轉52012、52013</div>
<div>傳真：06-2752453</div>
<div>E-mail：extncourse@email.ncku.edu.tw</div>`;

const duiConfigProps: IDuiContextProviderProps = {
  sidebarTitle: "",
  sidebarSubtitle: "",
  aboutWalkImgSrc: aboutImg,
  aboutWalkContent,
  credit,
  creditHref: "https://www.facebook.com/JRSHENDigitalCulture/",
  headerMBImgSrc: headerImgMb,
  headerDImgSrc: headerImg,
  menuSwitchItems: [{ id: "數位集章", name: "數位集章" }],
  weatherConfig: {
    token: "CWB-232A270E-12F1-4381-B9F2-DF2D2670A077",
    locations: [
      { county: "臺南市", town: "東區" },
      { county: "臺南市", town: "新化區" },
      { county: "臺南市", town: "中西區" },
      { county: "臺南市", town: "安南區" },
      { county: "臺南市", town: "北區" },
    ],
  },
  themeConfig: {
    "--dui-primary": "#54BD83",
    "--dui-accent": "#ED9228",
    "--dui-secondary": "#25A21B",
    "--dui-bg-secondary": "#EB9D1D",
    "--dui-bg-accent": "#54BD83",
  },
  settingConfig: {
    disabled: true, // 左側選單不出現「工具設定」
    // languageSwitcherDisabled: true, // 或：保留「工具設定」，只藏語言切換器
  },
};

// 景點的 icon、點擊開集章卡片、cluster、集章 key、`?id=` 深連結、
// 列表的定位與導航、底圖、線、面的顏色都由 jsdc-dui 依 Strapi 的資料建立。
// 這裡只留「點『成大人之道』的面，切到『路線介紹』選單」。
function App() {
  const { Jsdc } = useContext(JSDCContext);
  const dui = useContext(DuiContext);
  const { dgw } = useContext(DguidewalksContext);
  const menuSwitchRef = useRef(dui.menuSwitch);
  menuSwitchRef.current = dui.menuSwitch;

  useEffect(() => {
    (window as any).JSDC = Jsdc;
    return dgw.dataLoadEvent.addEventListener(() => {
      Jsdc.Controller.get("Layer")
        .getByName<GeoJSON>("成大人之道")
        ?.forEachLayerAsGeoJSON<Path>((layer) => {
          layer.on("click", () => menuSwitchRef.current("路線介紹"));
        });
    });
  }, [Jsdc, dgw]);

  return (
    <div className="root">
      <DguideWalksApp
        language="zh-TW"
        mainMenuChildren={
          <MenuItemWithDialog
            Icon={Checkin}
            title="數位集章"
            active={dui.activeMenuId === "數位集章"}
            {...dui.menuSwitcherAction("數位集章")}
          >
            {dui.activeMenuId === "數位集章" && (
              <iframe
                title="checkin list"
                style={{ height: "100%", border: 0, borderRadius: "5px" }}
                src={`https://map.jsdc.com.tw/tools/checkin/${EVENT_ID}/showlist?a=${EVENT_ID}`}
              ></iframe>
            )}
          </MenuItemWithDialog>
        }
      />
    </div>
  );
}

const config = new ConfigProvider({ eventId: EVENT_ID });

const AppWrapper: React.FC = () => {
  const [Jsdc] = useState(
    new JSDC(EVENT_ID, {
      bound: latLngBounds(
        latLng(22.95697073644188, 120.17051696777345),
        latLng(23.09489138501664, 120.33599853515626),
      ),
      maxZoom: 19,
    }),
  );

  return (
    <JSDCProvider Jsdc={Jsdc}>
      <DguidewalksProvider Jsdc={Jsdc} config={config}>
        <DuiContextProvider
          {...duiConfigProps}
          sceneConfig={{ checkinKanbanImgSrc: kanbanImg }}
        >
          <App />
        </DuiContextProvider>
      </DguidewalksProvider>
    </JSDCProvider>
  );
};

export default AppWrapper;
